import {
  AIR, ARCTIC_TRUCK, COMMENT_LOGICAL, COMMENT_MANUFACTURE, COMMENT_SERVICE, CONNECTION_TYPE, CREATED_AT,
  DISTANCE, MANUFACTURE_CONNECTION_TYPE, MONETARY_VALUES, MONETARY_VOLUMES, NAME, OWNERSHIP_PERCENTAGE,
  QUANTITY, QUESTION_COUNT, RAIL,
  RIGID_TRUCK, SEA, TRUCK, TYPE_OF_SERVICE_PROVIDER, UNIT_CURRENCY, UNIT_CURRENCY_VALUES,
  UNIT_MONETARY_VOLUMES, UPDATED_AT, VAN,
} from './formFields';

interface HeaderCell {
  id: string,
  label: string,
  withSort: boolean,
  isFirstCell?: boolean,
  isLastCell?: boolean,
}

interface TableConfig {
  prefix: string,
  fields: Array<{
    id: string,
    labelKey: string,
    orderBy?: string,
  }>,
}

const createHeaderCells = (config: TableConfig): HeaderCell[] => config.fields.map((field, index, array) => ({
  id: field.id,
  label: `${config.prefix}.${field.labelKey}`,
  withSort: field.id !== 'actions',
  ...(index === 0 && { isFirstCell: true }),
  ...(index === array.length - 1 && { isLastCell: true }),
}));

const PROFIT_CENTRES_CONFIG: TableConfig = {
  prefix: 'profitCentres.fieldsHeaders',
  fields: [
    { labelKey: 'logicalName', id: 'name' },
    { labelKey: 'uniqueProfitCentre', id: 'uniqueIdentifier' },
    { labelKey: 'valueChainPosition', id: 'valueChainSection' },
    { labelKey: 'ownership', id: 'ownership' },
    { labelKey: 'equityShareholding', id: 'equityShareholding' },
    { labelKey: 'financialControl', id: 'financialControl' },
    { labelKey: 'numberOfEmployees', id: 'numberOfEmployees' },
    { labelKey: 'dateOpened', id: 'dateOpened' },
    { labelKey: 'dateClosed', id: 'dateClosed' },
    { labelKey: 'sector', id: 'naceCode' },
    { labelKey: 'industry', id: 'naceSection' },
    { labelKey: 'euTaxonomyActivityCode', id: 'euTaxonomyCode' },
    { labelKey: 'fyStartDate', id: 'startDate' },
    { labelKey: 'fyEndDate', id: 'endDate' },
    { labelKey: 'relatedBalanceSheetAssets', id: 'netBookValue' },
    { labelKey: 'actualRevenue', id: 'totalRevenueRolling12MActual' },
    { labelKey: 'totalRevenue', id: 'totalRevenueRolling12MActual' },
    { labelKey: 'actualGrossProfit', id: 'totalGrossMarginRolling12MActual' },
    { labelKey: 'totalGrossProfit', id: 'totalGrossMarginRolling12MActual' },
    { labelKey: 'stock', id: 'wipFinishedGoodsStock' },
    { labelKey: 'countryOfOperation', id: 'addressCountry' },
    { labelKey: 'stateRegionCounty', id: 'addressState' },
    { labelKey: 'city', id: 'addressCity' },
    { labelKey: 'postCode', id: 'addressPostCode' },
    { labelKey: 'street', id: 'addressStreet' },
    { labelKey: 'buildingName', id: 'addressBuilding' },
    { labelKey: 'coordinates', id: 'addressCoordinates' },
    { labelKey: 'actions', id: 'actions' },
  ],
};

const OWN_OPERATIONS_CONFIG: TableConfig = {
  prefix: 'ownOperations.fields',
  fields: [
    { id: 'name', labelKey: 'name' },
    { id: 'uniqueIdentifier', labelKey: 'uniqueIdentifier' },
    { id: 'legalName', labelKey: 'legalEntity' },
    { id: 'valueChainSection', labelKey: 'valueChain' },
    { id: 'ownership', labelKey: 'ownership' },
    { id: 'equityShareholding', labelKey: 'equityShareholding' },
    { id: 'financialControl', labelKey: 'financialControl' },
    { id: 'assetCategory', labelKey: 'assetCategory' },
    { id: 'ownedLeased', labelKey: 'owned' },
    { id: 'numberOfEmployees', labelKey: 'employees' },
    { id: 'dateOpened', labelKey: 'dateOpened' },
    { id: 'dateClosed', labelKey: 'dateClosed' },
    { id: 'naceCode', labelKey: 'sector' },
    { id: 'naceSection', labelKey: 'industry' },
    { id: 'euTaxonomyCode', labelKey: 'taxonomy' },
    { id: 'profitCenterIds', labelKey: 'profitCentre' },
    { id: 'startDate', labelKey: 'startDate' },
    { id: 'endDate', labelKey: 'endDate' },
    { id: 'netBookValue', labelKey: 'netBook' },
    { id: 'addressCountry', labelKey: 'country' },
    { id: 'addressState', labelKey: 'state' },
    { id: 'addressCity', labelKey: 'town' },
    { id: 'addressPostCode', labelKey: 'post' },
    { id: 'addressStreet', labelKey: 'street' },
    { id: 'addressBuilding', labelKey: 'building' },
    { id: 'addressCoordinates', labelKey: 'coordinates' },
    { id: 'actions', labelKey: 'actions' },
  ],
};

const SUPPLIERS_CONFIG: TableConfig = {
  prefix: 'suppliers.fields',
  fields: [
    { id: 'name', labelKey: 'supplierName' },
    { id: 'uniqueIdentifier', labelKey: 'uniqueIdentifier' },
    { id: 'code', labelKey: 'supplierCode' },
    { id: 'valueChainSection', labelKey: 'valueChain' },
    { id: 'upstreamTier', labelKey: 'upstreamTier' },
    { id: 'ownership', labelKey: 'ownership' },
    { id: 'businessUnitType', labelKey: 'physicalOrLogical' },
    { id: 'equityShareholding', labelKey: 'equityShareholding' },
    { id: 'financialControl', labelKey: 'financialControl' },
    { id: 'dateOpened', labelKey: 'dateOpened' },
    { id: 'dateClosed', labelKey: 'dateClosed' },
    { id: 'naceCode', labelKey: 'sector' },
    { id: 'naceSection', labelKey: 'industry' },
    { id: 'euTaxonomyCode', labelKey: 'taxonomy' },
    { id: 'profitCenterId', labelKey: 'profitCentreId' },
    { id: 'addressCountry', labelKey: 'country' },
    { id: 'addressState', labelKey: 'state' },
    { id: 'addressCity', labelKey: 'town' },
    { id: 'addressPostCode', labelKey: 'post' },
    { id: 'addressStreet', labelKey: 'street' },
    { id: 'addressBuilding', labelKey: 'building' },
    { id: 'addressCoordinates', labelKey: 'coordinates' },
    { id: 'actions', labelKey: 'actions' },
  ],
};

const CUSTOMERS_CONFIG: TableConfig = {
  prefix: 'customers.fields',
  fields: [
    { id: 'name', labelKey: 'customerName' },
    { id: 'uniqueIdentifier', labelKey: 'uniqueIdentifier' },
    { id: 'code', labelKey: 'customerCode' },
    { id: 'valueChainSection', labelKey: 'valueChain' },
    { id: 'ownership', labelKey: 'ownership' },
    { id: 'physicalOrLogical', labelKey: 'physicalOrLogical' },
    { id: 'equityShareholding', labelKey: 'equityShareholding' },
    { id: 'financialControl', labelKey: 'financialControl' },
    { id: 'dateOpened', labelKey: 'dateOpened' },
    { id: 'dateClosed', labelKey: 'dateClosed' },
    { id: 'priorityRating', labelKey: 'priorityRating' },
    { id: 'naceCode', labelKey: 'sector' },
    { id: 'naceSection', labelKey: 'industry' },
    { id: 'euTaxonomyCode', labelKey: 'euTaxonomyActivityCode' },
    { id: 'profitCenterId', labelKey: 'profitCentreId' },
    { id: 'addressCountry', labelKey: 'country' },
    { id: 'addressState', labelKey: 'state' },
    { id: 'addressCity', labelKey: 'town' },
    { id: 'addressPostCode', labelKey: 'post' },
    { id: 'addressStreet', labelKey: 'street' },
    { id: 'addressBuilding', labelKey: 'building' },
    { id: 'addressCoordinates', labelKey: 'coordinates' },
    { id: 'actions', labelKey: 'actions' },
  ],
};

const PRODUCTS_CONFIG: TableConfig = {
  prefix: 'products.fields',
  fields: [
    { id: 'name', labelKey: 'productName' },
    { id: 'code', labelKey: 'productCode' },
    { id: 'uniqueIdentifier', labelKey: 'uniqueIdentifier' },
    { id: 'productCategory', labelKey: 'productCategory' },
    { id: 'suppliers', labelKey: 'supplier' },
    { id: 'valueChainSection', labelKey: 'valueChain' },
    { id: 'upstreamTier', labelKey: 'upstreamTier' },
    { id: 'dateOpened', labelKey: 'dateFrom' },
    { id: 'dateClosed', labelKey: 'dateTo' },
    { id: 'volumeQty', labelKey: 'volume' },
    { id: 'relianceOnFreeNaturalResources', labelKey: 'relianceNatural' },
    { id: 'relianceOnFreeSocialResources', labelKey: 'relianceSocial' },
    { id: 'criticalityRating', labelKey: 'rating' },
    { id: 'circularityFromDownstreamWaste', labelKey: 'circularityPercentage' },
    { id: 'profitCenterId', labelKey: 'profitCentreId' },
    { id: 'actions', labelKey: 'actions' },

  ],
};

export const DATA_SETS_PROFIT_CENTRES_HEAD_CELLS = createHeaderCells(PROFIT_CENTRES_CONFIG);
export const DATA_SETS_OWN_OPERATIONS_HEAD_CELLS = createHeaderCells(OWN_OPERATIONS_CONFIG);
export const DATA_SETS_SUPPLIERS_HEAD_CELLS = createHeaderCells(SUPPLIERS_CONFIG);
export const DATA_SETS_CUSTOMERS_HEAD_CELLS = createHeaderCells(CUSTOMERS_CONFIG);
export const DATA_SETS_PRODUCTS_HEAD_CELLS = createHeaderCells(PRODUCTS_CONFIG);

export const DATA_SET_PAGINATION_COUNT = 50;
export const DATA_SET_PAGINATION_COUNT_TASK_ACTIVITIES = 10;
export const SORT_DIRECTION_INDICATOR = '-';
export const DATA_OFFSET = 0;

export const DATA_SETS_ENUM_TYPES = {
  currency: 'currency',
  ownership: 'ownership',
  valueChainSection: 'valueChainSection',
  businessUnitType: 'businessUnitType',
  assetCategory: 'assetCategory',
  ownedLeased: 'ownedLeased',
  upstreamTier: 'upstreamTier',
  volumeQtyUnit: 'volumeQtyUnit',
  relianceOnFreeNaturalResources: 'relianceOnFreeNaturalResources',
  relianceOnFreeSocialResources: 'relianceOnFreeSocialResources',
  criticalityRating: 'criticalityRating',
  priorityRating: 'priorityRating',
} as const;

export const UID_NAME_TUPLES = {
  profitCenter: 'profit-center',
  ownOperation: 'own-operation',
  supplier: 'supplier',
  customer: 'customer',
  product: 'product',
  balanceSheetAsset: 'balance-sheet-asset',
} as const;

export const DATA_SET_MAX_MENU_HEIGHT = 164;

export const BUSINESS_UNIT_TYPE_REQUIRED_VALUE = 'Physical';
export const BUSINESS_UNIT_TYPE_LOGICAL = 'Logical';
export const BUSINESS_UNIT_TYPE_LOGICAL_VALUE = {
  label: BUSINESS_UNIT_TYPE_LOGICAL,
  value: BUSINESS_UNIT_TYPE_LOGICAL,
};
export const UPSTREAM_TIER_REQUIRED_VALUE = 'Tier 1';
export const TABLES_REQUIRED_VALUE = 'Tables';

export const VALUE_CHAIN_SECTION_DEFAULT_VALUES = {
  supplier: 'Upstream. Supplier Tier 1',
  customers: 'Downstream. End User',
  ownOperations: 'Own Operations. Value Add',
  product: 'Upstream. Input Product Tier 1',
  profitCentres: 'Own Operations. Output Product',
};

export const SEARCH_PARAMS = {
  search: 'search',
  offset: 'offset',
  isArchived: 'isArchived',
  description: 'description',
};

export const DATA_SETS_MAP_DEFAULT_VALUES = {
  Main: {
    map: [],
  },
};

export const DATA_SETS_MAP_PRODUCT_SUPPLIERS = {
  dryRun: false,
  Main: {
    source: '',
    map: [],
  },
  'Product Suppliers': {
    source: '',
    map: [],
  },
  'Profit Centres': {
    source: '',
    map: [],
  },
};

export const DATA_SETS_MAP_SUPPLIERS = {
  dryRun: false,
  Main: {
    source: '',
    map: [],
  },
  'Profit Centres': {
    source: '',
    map: [],
  },
};

export const PRODUCT_SUPPLIERS_SOURCE = 'Product Suppliers';
export const PROFIT_CENTRES_SOURCE = 'Profit Centres';

export const CONNECTION_TYPE_OPTIONS = [
  {
    label: 'Associate/Partner',
    value: 'associatePartner',
  },
  {
    label: 'Joint venture',
    value: 'jointVenture',
  },
  {
    label: 'Other related entity type',
    value: 'other',
  },
];

export const MANUFACTURE_CONNECTION_TYPE_OPTIONS = [
  {
    label: 'Contract manufactured',
    value: 'contractManufactured',
  },
  {
    label: 'In house manufactured',
    value: 'inHouseManufactured',
  },
];

export const TYPE_OF_SERVICE_PROVIDER_OPTIONS = [
  {
    label: 'Professional',
    value: 'professional',
  },
  {
    label: 'Technical',
    value: 'technical',
  },
  {
    label: 'Manual',
    value: 'manual',
  },
  {
    label: 'Adviser',
    value: 'adviser',
  },
  {
    label: 'Consultant',
    value: 'consultant',
  },
  {
    label: 'Legal',
    value: 'legal',
  },
  {
    label: 'Other',
    value: 'other',
  },
];

export const TYPE_CONNECTION_FORM = {
  logical: false,
  supplier: false,
  service: false,
  manufacture: false,
} as const;

export const TYPE_CONNECTION_FORM_OPTIONS = {
  logical: 'logical',
  supplier: 'supplier',
  service: 'service',
  manufacture: 'manufacture',
} as const;

export const CONNECTION_VC_LOGIC_FORM_FIELDS = [
  CONNECTION_TYPE, OWNERSHIP_PERCENTAGE, COMMENT_LOGICAL,
];

export const CONNECTION_VC_SUPPLIER_FORM_FIELDS = [
  AIR, ARCTIC_TRUCK, DISTANCE, MONETARY_VOLUMES, QUANTITY, RAIL,
  RIGID_TRUCK, SEA, TRUCK, UNIT_CURRENCY, UNIT_MONETARY_VOLUMES, VAN,
];

export const CONNECTION_VC_SERVICE_FORM_FIELDS = [
  COMMENT_SERVICE, MONETARY_VALUES, TYPE_OF_SERVICE_PROVIDER, UNIT_CURRENCY_VALUES,
];

export const CONNECTION_VC_MANUFACTURE_FORM_FIELDS = [
  MANUFACTURE_CONNECTION_TYPE, COMMENT_MANUFACTURE,
];

export const ALL_CONNECTION_VC_FORM_FIELDS = [
  ...CONNECTION_VC_LOGIC_FORM_FIELDS,
  ...CONNECTION_VC_SUPPLIER_FORM_FIELDS,
  ...CONNECTION_VC_SERVICE_FORM_FIELDS,
  ...CONNECTION_VC_MANUFACTURE_FORM_FIELDS,
];

export const JOINT_VENTURE = 'Joint venture';
export const SUPPLIER_OF_GOODS = 'supplierOfGoods';

export const CONNECTION_ACTION = {
  CREATE: 'create',
  UPDATE: 'update',
  DELETE: 'delete',
} as const;

export const NAME_FILTERS = {
  OWN_OPERATION: 'ownOperations',
  CONNECTION: 'connections',
  PROFIT_CENTER: 'profitCenters',
  PRODUCT: 'products',
  CUSTOMER: 'customers',
  SUPPLIER: 'suppliers',
  GEO_PATH: 'geoPaths',
  ORG_PATH: 'orgPaths',
  REVENUE: 'revenue',
  UNIQUE_IDENTIFIER: 'uniqueIdentifierFilters',
  COUNTRY_CODE: 'countryCode',
  BALANCE_SHEET_ASSET: 'balanceSheetAssets',
  ASSETS: 'assets',
  PROFIT_CENTER_LONG: 'profit-center',
  ORG_STRUCTURE: 'organizationalStructure',
};

export const NAME_INPUTS_DEFAULT = {
  SEARCH: 'search',
};

export const VALUE_CHAIN_SECTION_OPTIONS = [
  {
    value: 'Upstream. Supplier Tier 2',
    label: 'Upstream. Supplier Tier 2',
  },
  {
    value: 'Upstream. Input Product Tier 2',
    label: 'Upstream. Input Product Tier 2',
  },
  {
    value: 'Upstream. Supplier Tier 1',
    label: 'Upstream. Supplier Tier 1',
  },
  {
    value: 'Upstream. Input Product Tier 1',
    label: 'Upstream. Input Product Tier 1',
  },
  {
    value: 'Own Operations. Value Add',
    label: 'Own Operations. Value Add',
  },
  {
    value: 'Own Operations. Output product',
    label: 'Own Operations. Output product',
  },
  {
    value: 'Own Operations. Downstream Internal',
    label: 'Own Operations. Downstream Internal',
  },
  {
    value: 'Downstream. Logistics and Distribution',
    label: 'Downstream. Logistics and Distribution',
  },
  {
    value: 'Downstream. End User',
    label: 'Downstream. End User',
  },
  {
    value: 'Downstream. End of Life',
    label: 'Downstream. End of Life',
  },
];

export const OWNERSHIP_OPTIONS = [
  {
    value: 'Subsidiary',
    label: 'Subsidiary',
  },
  {
    value: 'JV',
    label: 'JV',
  },
  {
    value: 'Associate Subsidiary',
    label: 'Associate Subsidiary',
  },
  {
    value: 'Division',
    label: 'Division',
  },
  {
    value: 'Business Unit',
    label: 'Business Unit',
  },
];

export const NACE_SECTION_OPTIONS = [
  {
    value: 'A',
    label: 'A - AGRICULTURE, FORESTRY AND FISHING',
  },
  {
    value: 'B',
    label: 'B - MINING AND QUARRYING',
  },
  {
    value: 'C',
    label: 'C - MANUFACTURING',
  },
  {
    value: 'D',
    label: 'D - ELECTRICITY, GAS, STEAM AND AIR CONDITIONING SUPPLY',
  },
  {
    value: 'E',
    label: 'E - WATER SUPPLY; SEWERAGE, WASTE MANAGEMENT AND REMEDIATION ACTIVITIES',
  },
  {
    value: 'F',
    label: 'F - CONSTRUCTION',
  },
  {
    value: 'G',
    label: 'G - WHOLESALE AND RETAIL TRADE',
  },
  {
    value: 'H',
    label: 'H - TRANSPORTATION AND STORAGE',
  },
  {
    value: 'I',
    label: 'I - ACCOMMODATION AND FOOD SERVICE ACTIVITIES',
  },
  {
    value: 'J',
    label: 'J - PUBLISHING, BROADCASTING, AND CONTENT PRODUCTION AND DISTRIBUTION ACTIVITIES',
  },
  {
    value: 'K',
    label: 'K - TELECOMMUNICATION, COMPUTER PROGRAMMING, CONSULTING, COMPUTING INFRASTRUCTURE '
      + 'AND OTHER INFORMATION SERVICE ACTIVITIES',
  },
  {
    value: 'L',
    label: 'L - FINANCIAL AND INSURANCE ACTIVITIES',
  },
  {
    value: 'M',
    label: 'M - REAL ESTATE ACTIVITIES',
  },
  {
    value: 'N',
    label: 'N - PROFESSIONAL, SCIENTIFIC AND TECHNICAL ACTIVITIES',
  },
  {
    value: 'O',
    label: 'O - ADMINISTRATIVE AND SUPPORT SERVICE ACTIVITIES',
  },
  {
    value: 'P',
    label: 'P - PUBLIC ADMINISTRATION AND DEFENCE; COMPULSORY SOCIAL SECURITY',
  },
  {
    value: 'Q',
    label: 'Q - EDUCATION',
  },
  {
    value: 'R',
    label: 'R - HUMAN HEALTH AND SOCIAL WORK ACTIVITIES',
  },
  {
    value: 'S',
    label: 'S - ARTS, SPORTS AND RECREATION',
  },
  {
    value: 'T',
    label: 'T - OTHER SERVICE ACTIVITIES',
  },
  {
    value: 'U',
    label: 'U - ACTIVITIES OF HOUSEHOLDS AS EMPLOYERS AND UNDIFFERENTIATED GOODS - '
      + 'AND SERVICE-PRODUCING ACTIVITIES OF HOUSEHOLDS FOR OWN USE',
  },
  {
    value: 'V',
    label: 'V - ACTIVITIES OF EXTRATERRITORIAL ORGANISATIONS AND BODIES',
  },
];

export const NACE_CODE_OPTIONS = [
  {
    value: '01',
    label: 'A01 - Crop and animal production, hunting and related service activities',
  },
  {
    value: '02',
    label: 'A02 - Forestry and logging',
  },
  {
    value: '03',
    label: 'A03 - Fishing and aquaculture',
  },
  {
    value: '05',
    label: 'B05 - Mining of coal and lignite',
  },
  {
    value: '06',
    label: 'B06 - Extraction of crude petroleum and natural gas',
  },
  {
    value: '07',
    label: 'B07 - Mining of metal ores',
  },
  {
    value: '08',
    label: 'B08 - Other mining and quarrying',
  },
  {
    value: '09',
    label: 'B09 - Mining support service activities',
  },
  {
    value: '10',
    label: 'C10 - Manufacture of food products',
  },
  {
    value: '11',
    label: 'C11 - Manufacture of beverages',
  },
  {
    value: '12',
    label: 'C12 - Manufacture of tobacco products',
  },
  {
    value: '13',
    label: 'C13 - Manufacture of textiles',
  },
  {
    value: '14',
    label: 'C14 - Manufacture of wearing apparel',
  },
  {
    value: '15',
    label: 'C15 - Manufacture of leather and related products of other materials',
  },
  {
    value: '16',
    label: 'C16 - Manufacture of wood and of products of wood and cork, except furniture; '
      + 'manufacture of articles of straw and plaiting materials',
  },
  {
    value: '17',
    label: 'C17 - Manufacture of paper and paper products',
  },
  {
    value: '18',
    label: 'C18 - Printing and reproduction of recorded media',
  },
  {
    value: '19',
    label: 'C19 - Manufacture of coke and refined petroleum products',
  },
  {
    value: '20',
    label: 'C20 - Manufacture of chemicals and chemical products',
  },
  {
    value: '21',
    label: 'C21 - Manufacture of basic pharmaceutical products and pharmaceutical preparations',
  },
  {
    value: '22',
    label: 'C22 - Manufacture of rubber and plastic products',
  },
  {
    value: '23',
    label: 'C23 - Manufacture of other non-metallic mineral products',
  },
  {
    value: '24',
    label: 'C24 - Manufacture of basic metals',
  },
  {
    value: '25',
    label: 'C25 - Manufacture of fabricated metal products, except machinery and equipment',
  },
  {
    value: '26',
    label: 'C26 - Manufacture of computer, electronic and optical products',
  },
  {
    value: '27',
    label: 'C27 - Manufacture of electrical equipment',
  },
  {
    value: '28',
    label: 'C28 - Manufacture of machinery and equipment n.e.c.',
  },
  {
    value: '29',
    label: 'C29 - Manufacture of motor vehicles, trailers and semi-trailers',
  },
  {
    value: '30',
    label: 'C30 - Manufacture of other transport equipment',
  },
  {
    value: '31',
    label: 'C31 - Manufacture of furniture',
  },
  {
    value: '32',
    label: 'C32 - Other manufacturing',
  },
  {
    value: '33',
    label: 'C33 - Repair, maintenance and installation of machinery and equipment',
  },
  {
    value: '35',
    label: 'D35 - Electricity, gas, steam and air conditioning supply',
  },
  {
    value: '36',
    label: 'E36 - Water collection, treatment and supply',
  },
  {
    value: '37',
    label: 'E37 - Sewerage',
  },
  {
    value: '38',
    label: 'E38 - Waste collection, recovery and disposal activities',
  },
  {
    value: '39',
    label: 'E39 - Remediation activities and other waste management service activities',
  },
  {
    value: '41',
    label: 'F41 - Construction of residential and non-residential buildings',
  },
  {
    value: '42',
    label: 'F42 - Civil engineering',
  },
  {
    value: '43',
    label: 'F43 - Specialised construction activities',
  },
  {
    value: '46',
    label: 'G46 - Wholesale trade',
  },
  {
    value: '47',
    label: 'G47 - Retail trade',
  },
  {
    value: '49',
    label: 'H49 - Land transport and transport via pipelines',
  },
  {
    value: '50',
    label: 'H50 - Water transport',
  },
  {
    value: '51',
    label: 'H51 - Air transport',
  },
  {
    value: '52',
    label: 'H52 - Warehousing, storage and support activities for transportation',
  },
  {
    value: '53',
    label: 'H53 - Postal and courier activities',
  },
  {
    value: '55',
    label: 'I55 - Accommodation',
  },
  {
    value: '56',
    label: 'I56 - Food and beverage service activities',
  },
  {
    value: '58',
    label: 'J58 - Publishing activities',
  },
  {
    value: '59',
    label: 'J59 - Motion picture, video and television programme production, '
      + 'sound recording and music publishing activities',
  },
  {
    value: '60',
    label: 'J60 - Programming, broadcasting, news agency and other content distribution activities',
  },
  {
    value: '61',
    label: 'K61 - Telecommunication',
  },
  {
    value: '62',
    label: 'K62 - Computer programming, consultancy and related activities',
  },
  {
    value: '63',
    label: 'K63 - Computing infrastructure, data processing, hosting and other information service activities',
  },
  {
    value: '64',
    label: 'L64 - Financial service activities, except insurance and pension funding',
  },
  {
    value: '65',
    label: 'L65 - Insurance, reinsurance and pension funding, except compulsory social security',
  },
  {
    value: '66',
    label: 'L66 - Activities auxiliary to financial services and insurance activities',
  },
  {
    value: '68',
    label: 'M68 - Real estate activities',
  },
  {
    value: '69',
    label: 'N69 - Legal and accounting activities',
  },
  {
    value: '70',
    label: 'N70 - Activities of head offices and management consultancy',
  },
  {
    value: '71',
    label: 'N71 - Architectural and engineering activities; technical testing and analysis',
  },
  {
    value: '72',
    label: 'N72 - Scientific research and development',
  },
  {
    value: '73',
    label: 'N73 - Activities of advertising, market research and public relations',
  },
  {
    value: '74',
    label: 'N74 - Other professional, scientific and technical activities',
  },
  {
    value: '75',
    label: 'N75 - Veterinary activities',
  },
  {
    value: '77',
    label: 'O77 - Rental and leasing activities',
  },
  {
    value: '78',
    label: 'O78 - Employment activities',
  },
  {
    value: '79',
    label: 'O79 - Travel agency, tour operator and other reservation service and related activities',
  },
  {
    value: '80',
    label: 'O80 - Investigation and security activities',
  },
  {
    value: '81',
    label: 'O81 - Services to buildings and landscape activities',
  },
  {
    value: '82',
    label: 'O82 - Office administrative, office support and other business support activities',
  },
  {
    value: '84',
    label: 'P84 - Public administration and defence; compulsory social security',
  },
  {
    value: '85',
    label: 'Q85 - Education',
  },
  {
    value: '86',
    label: 'R86 - Human health activities',
  },
  {
    value: '87',
    label: 'R87 - Residential care activities',
  },
  {
    value: '88',
    label: 'R88 - Social work activities without accommodation',
  },
  {
    value: '90',
    label: 'S90 - Arts creation and performing arts activities',
  },
  {
    value: '91',
    label: 'S91 - Libraries, archives, museums and other cultural activities',
  },
  {
    value: '92',
    label: 'S92 - Gambling and betting activities',
  },
  {
    value: '93',
    label: 'S93 - Sports activities and amusement and recreation activities',
  },
  {
    value: '94',
    label: 'T94 - Activities of membership organisations',
  },
  {
    value: '95',
    label: 'T95 - Repair and maintenance of computers, personal and household goods, '
      + 'and motor vehicles and motorcycles',
  },
  {
    value: '96',
    label: 'T96 - Personal service activities',
  },
  {
    value: '97',
    label: 'U97 - Activities of households as employers of domestic personnel',
  },
  {
    value: '98',
    label: 'U98 - Undifferentiated goods- and service-producing activities of private households for own use',
  },
  {
    value: '99',
    label: 'V99 - Activities of extraterritorial organisations and bodies',
  },
];

export const ID_VALUE = 'id';

export const MILLION = 1000000;

export const DATA_NEW_PROFIT_SUPPLIERS = {
  profitCenterId: '',
  startDate: '',
  endDate: '',
  totalCost: '',
  percentOfTotalCost: null,
};

export const TABLE_HEADERS_SUPPLIERS = [
  'common.profitCentreLabel',
  'common.startDate',
  'common.endDate',
  'common.supplierTotalCost',
  'common.supplierPercentOfTotalCost',
];

export const TABLE_HEADERS_PRODUCTS = [
  'common.profitCentreLabel',
  'common.startDate',
  'common.endDate',
  'common.supplierTotalProductCost',
  'common.supplierPercentOfTotalProductCost',
  'common.expectedGrowthDecline',
];

export const TABLE_HEADERS_CUSTOMERS = [
  'common.profitCentreLabel',
  'common.startDate',
  'common.endDate',
  'customers.fields.actualRevenue',
  'customers.fields.totalRevenue',
];

const TASK_HEADER_CONFIG: TableConfig = {
  prefix: 'taskManagement.fields',
  fields: [
    { id: 'name', labelKey: 'taskName' },
    { id: 'relatedTo', labelKey: 'relatedTo' },
    { id: 'priority', labelKey: 'priority' },
    { id: 'status', labelKey: 'status' },
    { id: 'actions', labelKey: 'actions' },

  ],
};

export const TASK_HEAD_CELLS = createHeaderCells(TASK_HEADER_CONFIG);

const TASK_HEADER_INFO_CONFIG: TableConfig = {
  prefix: 'taskManagement.fields',
  fields: [
    { id: 'name', labelKey: 'taskName' },
    { id: 'assignedTo', labelKey: 'assignedTo' },
    { id: 'createdDate', labelKey: 'createdDate' },
    { id: 'startDate', labelKey: 'startDate' },
    { id: 'closedDate', labelKey: 'closedDate' },
    { id: 'priority', labelKey: 'priority' },
    { id: 'status', labelKey: 'status' },
    { id: 'actions', labelKey: 'actions' },
  ],
};

export const TASK_HEAD_INFO_CELLS = createHeaderCells(TASK_HEADER_INFO_CONFIG);

const TASK_HEADER_ASSIGNED_USERS: TableConfig = {
  prefix: 'materialityAssessment.fields',
  fields: [
    { id: 'name', labelKey: 'name' },
    { id: 'role', labelKey: 'role' },
    { id: 'job', labelKey: 'job' },
    { id: 'action', labelKey: 'action' },
  ],
};

export const TASK_HEAD_ASSIGNED_USERS_CELLS = createHeaderCells(TASK_HEADER_ASSIGNED_USERS);

const INVESTORS_CONFIG: TableConfig = {
  prefix: 'materialityAssessment.fields',
  fields: [
    { id: 'investor', labelKey: 'investor' },
    { id: 'contactName', labelKey: 'contactName' },
    { id: 'contactEmail', labelKey: 'contactEmail' },
    { id: 'equity', labelKey: 'equity' },
    { id: 'shareCategory', labelKey: 'shareCategory' },
    { id: 'durationOfInvestment', labelKey: 'durationOfInvestment' },
    { id: 'actions', labelKey: 'actions' },
  ],
};

export const TASK_HEAD_INVESTORS_CELLS = createHeaderCells(INVESTORS_CONFIG);

const BOARD_CONFIG: TableConfig = {
  prefix: 'materialityAssessment.fields',
  fields: [
    { id: 'boardMemberName', labelKey: 'boardMemberName' },
    { id: 'boardMemberProfession', labelKey: 'boardMemberProfession' },
    { id: 'boardMemberCategory', labelKey: 'boardMemberCategory' },
    { id: 'contactEmail', labelKey: 'contactEmail' },
    { id: 'contactName', labelKey: 'contactName' },
    { id: 'actions', labelKey: 'actions' },
  ],
};

export const TASK_HEAD_BOARD_CELLS = createHeaderCells(BOARD_CONFIG);

const EMPLOYEES_CONFIG: TableConfig = {
  prefix: 'materialityAssessment.fields',
  fields: [
    { id: 'department', labelKey: 'department' },
    { id: 'role', labelKey: 'role' },
    { id: 'employeeRepresentativeName', labelKey: 'employeeRepresentativeName' },
    { id: 'contactEmail', labelKey: 'contactEmail' },
    { id: 'actions', labelKey: 'actions' },
  ],
};

export const TASK_HEAD_EMPLOYEES_CELLS = createHeaderCells(EMPLOYEES_CONFIG);

const VC_TABLE_CONFIG: TableConfig = {
  prefix: 'materialityAssessment.fields',
  fields: [
    { id: 'valueChainName', labelKey: 'valueChainName' },
    { id: 'createdBy', labelKey: 'createdBy' },
    { id: 'updatedBy', labelKey: 'updatedBy' },
  ],
};

export const TASK_HEAD_VC_TABLE_CELLS = createHeaderCells(VC_TABLE_CONFIG);

const COMMUNITIES_CONFIG: TableConfig = {
  prefix: 'materialityAssessment.fields',
  fields: [
    { id: 'communityGroupName', labelKey: 'communityGroupName' },
    { id: 'representationCategory', labelKey: 'representationCategory' },
    { id: 'representativeName', labelKey: 'representativeName' },
    { id: 'contactEmail', labelKey: 'contactEmail' },
    { id: 'internalLiaison', labelKey: 'internalLiaison' },
    { id: 'internalLiaisonEmail', labelKey: 'internalLiaisonEmail' },
    { id: 'actions', labelKey: 'actions' },
  ],
};

export const TASK_HEAD_COMMUNITIES_CELLS = createHeaderCells(COMMUNITIES_CONFIG);

const MEDIA_CONFIG: TableConfig = {
  prefix: 'materialityAssessment.fields',
  fields: [
    { id: 'mediaOrganisationName', labelKey: 'mediaOrganisationName' },
    { id: 'representationCategory', labelKey: 'representationCategory' },
    { id: 'relevanceToCompany', labelKey: 'relevanceToCompany' },
    { id: 'contactName', labelKey: 'contactName' },
    { id: 'contactEmail', labelKey: 'contactEmail' },
    { id: 'internalLiaison', labelKey: 'internalLiaison' },
    { id: 'internalLiaisonEmail', labelKey: 'internalLiaisonEmail' },
    { id: 'actions', labelKey: 'actions' },
  ],
};

export const MEDIA_COMMUNITIES_CELLS = createHeaderCells(MEDIA_CONFIG);

const NGO_CONFIG: TableConfig = {
  prefix: 'materialityAssessment.fields',
  fields: [
    { id: 'ngoName', labelKey: 'ngoName' },
    { id: 'representationCategory', labelKey: 'representationCategory' },
    { id: 'relevanceToCompany', labelKey: 'relevanceToCompany' },
    { id: 'ngoContactName', labelKey: 'ngoContactName' },
    { id: 'ngoContactEmail', labelKey: 'ngoContactEmail' },
    { id: 'internalLiaison', labelKey: 'internalLiaison' },
    { id: 'internalLiaisonEmail', labelKey: 'internalLiaisonEmail' },
    { id: 'actions', labelKey: 'actions' },
  ],
};

export const NGO_COMMUNITIES_CELLS = createHeaderCells(NGO_CONFIG);

const INVESTORS_CUSTOMERS_CONFIG: TableConfig = {
  prefix: 'materialityAssessment.fields',
  fields: [
    { id: 'question', labelKey: 'question' },
    { id: 'questionType', labelKey: 'questionType' },
    { id: 'questionSourceType', labelKey: 'questionSourceType' },
    { id: 'companyName', labelKey: 'companyName' },
    { id: 'source', labelKey: 'source' },
    { id: 'valueChainName', labelKey: 'valueChainName' },
  ],
};

export const INVESTORS_CUSTOMERS_CELLS = createHeaderCells(INVESTORS_CUSTOMERS_CONFIG);

const INVESTORS_CUSTOMERS_GROUP_CONFIG: TableConfig = {
  prefix: 'materialityAssessment.fields',
  fields: [
    { id: 'questionGroup', labelKey: 'questionGroup' },
    { id: 'questionType', labelKey: 'questionType' },
    { id: 'questionSourceType', labelKey: 'questionSourceType' },
    { id: 'companyName', labelKey: 'companyName' },
    { id: 'source', labelKey: 'source' },
    { id: 'valueChainName', labelKey: 'valueChainName' },
  ],
};

export const INVESTORS_CUSTOMERS_GROUP_CELLS = createHeaderCells(INVESTORS_CUSTOMERS_GROUP_CONFIG);

const COMPETITOR_CONFIG: TableConfig = {
  prefix: 'materialityAssessment.fields',
  fields: [
    { id: 'companyName', labelKey: 'companyName' },
    { id: 'stockTicker', labelKey: 'stockTicker' },
    { id: 'website', labelKey: 'website' },
    { id: 'sector', labelKey: 'sector' },
    { id: 'seerCompetitor', labelKey: 'seerCompetitor' },
    { id: 'size', labelKey: 'size' },
    { id: 'actions', labelKey: 'actions' },
  ],
};

export const TASK_HEAD_COMPETITOR_CELLS = createHeaderCells(COMPETITOR_CONFIG);

const MATERIALITY_ASSESSMENT_HEADER_CONFIG: TableConfig = {
  prefix: 'materialityAssessment.fields',
  fields: [
    { id: 'name', labelKey: 'name' },
    { id: 'created', labelKey: 'created' },
    { id: 'lastModified', labelKey: 'lastModified' },
    { id: 'status', labelKey: 'status' },
    { id: 'actions', labelKey: 'actions' },

  ],
};

export const MATERIALITY_ASSESSMENT_HEAD_CELLS = createHeaderCells(MATERIALITY_ASSESSMENT_HEADER_CONFIG);

const GEOGRAPHIC_CONFIG: TableConfig = {
  prefix: 'materialityAssessment.fields',
  fields: [
    { id: 'country', labelKey: 'country' },
    { id: 'categoryName', labelKey: 'category' },
    { id: 'source', labelKey: 'source' },
    { id: 'regions', labelKey: 'state' },
    { id: 'sensitivityName', labelKey: 'sensitivityName' },
    { id: 'action', labelKey: 'action' },
  ],
};

export const TASK_HEAD_GEOGRAPHIC_CELLS = createHeaderCells(GEOGRAPHIC_CONFIG);

const GEO_ANALYZE_CONFIG: TableConfig = {
  prefix: 'materialityAssessment.fields',
  fields: [
    { id: 'role', labelKey: 'role' },
    { id: 'valueChain', labelKey: 'valueChain' },
    { id: 'fullName', labelKey: 'fullName' },
    { id: 'industry', labelKey: 'industry' },
    { id: 'state', labelKey: 'state' },
  ],
};

export const TASK_HEAD_GEO_ANALYZE_CELLS = createHeaderCells(GEO_ANALYZE_CONFIG);

const GEOGRAPHIC_ANALYZE_CONFIG: TableConfig = {
  prefix: 'materialityAssessment.fields',
  fields: [
    { id: 'countryCode', labelKey: 'country' },
    { id: 'categoryNames', labelKey: 'category' },
    { id: 'sensitivityNames', labelKey: 'sensitivityName' },
    { id: 'regions', labelKey: 'state' },
  ],
};

export const TASK_HEAD_GEOGRAPHIC_ANALYZE_CELLS = createHeaderCells(GEOGRAPHIC_ANALYZE_CONFIG)
  .map((cell) => ({ ...cell, withSort: false }));

const INDUSTRY_CONFIG: TableConfig = {
  prefix: 'materialityAssessment.fields',
  fields: [
    { id: 'country', labelKey: 'country' },
    { id: 'naceSection', labelKey: 'sectorCode' },
    { id: 'industryCode', labelKey: 'industryCode' },
    { id: 'action', labelKey: 'action' },
  ],
};

export const TASK_HEAD_INDUSTRY_CELLS = createHeaderCells(INDUSTRY_CONFIG);

const INDUSTRY_ANALYZE_CONFIG: TableConfig = {
  prefix: 'materialityAssessment.fields',
  fields: [
    { id: 'country', labelKey: 'country' },
    { id: 'sectorCode', labelKey: 'sectorCode' },
    { id: 'industryCode', labelKey: 'industryCode' },
  ],
};

export const TASK_HEAD_INDUSTRY_ANALYZE_CELLS = createHeaderCells(INDUSTRY_ANALYZE_CONFIG);

const INDUSTRY_MINI_ANALYZE_CONFIG: TableConfig = {
  prefix: 'materialityAssessment.fields',
  fields: [
    { id: 'role', labelKey: 'role' },
    { id: 'fullName', labelKey: 'fullName' },
    { id: 'industry', labelKey: 'industry' },
    { id: 'state', labelKey: 'state' },
  ],
};

export const TASK_HEAD_INDUSTRY_MINI_ANALYZE_CELLS = createHeaderCells(INDUSTRY_MINI_ANALYZE_CONFIG);

const SECTOR_SPECIFIC_CONFIG: TableConfig = {
  prefix: 'materialityAssessment.fields',
  fields: [
    { id: 'country', labelKey: 'country' },
    { id: 'sectorCode', labelKey: 'sectorCode' },
    { id: 'industryCode', labelKey: 'industryCode' },
    { id: 'actions', labelKey: 'actions' },
  ],
};

export const TASK_HEAD_SECTOR_SPECIFIC_CELLS = createHeaderCells(SECTOR_SPECIFIC_CONFIG);

const ANALYSIS_CONFIG: TableConfig = {
  prefix: 'materialityAssessment.fields',
  fields: [
    { id: 'companyName', labelKey: 'companyName' },
    { id: 'stockTicker', labelKey: 'stockTicker' },
    { id: 'country', labelKey: 'country' },
    { id: 'sizeEmployees', labelKey: 'sizeEmployees' },
    { id: 'stakeholderType', labelKey: 'stakeholderType' },
    { id: 'supplierExpenses', labelKey: 'supplierExpenses' },
    { id: 'purchaseVolume', labelKey: 'purchaseVolume' },
    { id: 'profit', labelKey: 'profit' },
    { id: 'actions', labelKey: 'actions' },
  ],
};

export const TASK_HEAD_ANALYSIS_CELLS = createHeaderCells(ANALYSIS_CONFIG);

const MATERIALITY_ASSESSMENT_SURVEY_HEADER_CONFIG: TableConfig = {
  prefix: 'materialityAssessment.fields',
  fields: [
    { id: 'name', labelKey: 'name' },
    { id: 'description', labelKey: 'description' },
    { id: 'createdAt', labelKey: 'created' },
    { id: 'updatedAt', labelKey: 'lastModified' },
    { id: 'questionsCount', labelKey: 'questionsCount' },
    { id: 'status', labelKey: 'status' },
    { id: 'actions', labelKey: 'actions' },

  ],
};

const MATERIALITY_ASSESSMENT_SURVEY_HEADER_MODAL: TableConfig = {
  prefix: 'materialityAssessment.fields',
  fields: [
    { id: 'name', labelKey: 'name' },
    { id: 'description', labelKey: 'description' },
    { id: 'createdAt', labelKey: 'created' },
    { id: 'updatedAt', labelKey: 'lastModified' },
    { id: 'questionsCount', labelKey: 'questionsCount' },
    { id: 'status', labelKey: 'status' },
  ],
};

export const MATERIALITY_ASSESSMENT_SURVEY_HEAD_CELLS_MODAL = createHeaderCells(
  MATERIALITY_ASSESSMENT_SURVEY_HEADER_MODAL,
);

const createHeaderCellsFilterSort = (config: TableConfig, showSort: String[]): HeaderCell[] => config.fields
  .map((field, index, array) => ({
    id: field.id,
    label: `${config.prefix}.${field.labelKey}`,
    withSort: showSort.includes(field.id),
    ...(index === 0 && { isFirstCell: true }),
    ...(index === array.length - 1 && { isLastCell: true }),
  }));

export const MATERIALITY_ASSESSMENT_SURVEY_HEAD_CELLS = createHeaderCellsFilterSort(
  MATERIALITY_ASSESSMENT_SURVEY_HEADER_CONFIG,
  [NAME, CREATED_AT, UPDATED_AT, QUESTION_COUNT],
);

const MATERIALITY_ASSESSMENT_SURVEY_ANSWER_HEADER_CONFIG: TableConfig = {
  prefix: 'materialityAssessment.fields',
  fields: [
    { id: 'name', labelKey: 'name' },
    { id: 'description', labelKey: 'description' },
    { id: 'respondent', labelKey: 'respondent' },
    { id: 'valueChainName', labelKey: 'valueChainName' },
    { id: 'status', labelKey: 'status' },
    { id: 'createdAt', labelKey: 'created' },
    { id: 'actions', labelKey: 'actions' },
  ],
};

export const MATERIALITY_ASSESSMENT_SURVEY_ANSWER_HEAD_CELLS = createHeaderCells(
  MATERIALITY_ASSESSMENT_SURVEY_ANSWER_HEADER_CONFIG,
);

const SUPPLIERS_STAKEHOLDERS_CONFIG: TableConfig = {
  prefix: 'materialityAssessment.fields',
  fields: [
    { id: 'valueChainName', labelKey: 'valueChainName' },
    { id: 'created', labelKey: 'created' },
    { id: 'lastModified', labelKey: 'lastModified' },
    { id: 'status', labelKey: 'status' },
  ],
};

export const TASK_SUPPLIERS_STAKEHOLDERS_CONFIG_CELLS = createHeaderCells(SUPPLIERS_STAKEHOLDERS_CONFIG);

const SUPPLIERS_MINI_STAKEHOLDERS_CONFIG: TableConfig = {
  prefix: 'materialityAssessment.fields',
  fields: [
    { id: 'checkbox', labelKey: 'checkbox' },
    { id: 'supplierName', labelKey: 'supplierName' },
    { id: 'supplierSystemUniqueIdentifier', labelKey: 'supplierSystemUniqueIdentifier' },
    { id: 'supplierCode', labelKey: 'supplierCode' },
    { id: 'valueChainPosition', labelKey: 'valueChainPosition' },
    { id: 'upstreamTier', labelKey: 'upstreamTier' },
    { id: 'ownership', labelKey: 'ownership' },
  ],
};

export const TASK_SUPPLIERS_MINI_STAKEHOLDERS_CONFIG_CELLS = createHeaderCells(SUPPLIERS_MINI_STAKEHOLDERS_CONFIG);

const CUSTOMERS_MINI_STAKEHOLDERS_CONFIG: TableConfig = {
  prefix: 'customers.fields',
  fields: [
    { id: 'checkbox', labelKey: 'checkbox' },
    { id: 'customerName', labelKey: 'customerName' },
    { id: 'customerSystemUniqueIdentifier', labelKey: 'uniqueIdentifier' },
    { id: 'customerCode', labelKey: 'customerCode' },
    { id: 'valueChainPosition', labelKey: 'valueChain' },
    { id: 'upstreamTier', labelKey: 'upstreamTier' },
    { id: 'ownership', labelKey: 'ownership' },
  ],
};

export const TASK_CUSTOMERS_MINI_STAKEHOLDERS_CONFIG_CELLS = createHeaderCells(CUSTOMERS_MINI_STAKEHOLDERS_CONFIG);

const SUPPLIERS_STAKEHOLDERS: TableConfig = {
  prefix: 'suppliers.fields',
  fields: [
    { id: 'name', labelKey: 'supplierName' },
    { id: 'uniqueIdentifier', labelKey: 'uniqueIdentifier' },
    { id: 'code', labelKey: 'supplierCode' },
    { id: 'sourceVCMId', labelKey: 'sourceVCMId' },
    { id: 'valueChainSection', labelKey: 'valueChain' },
    { id: 'upstreamTier', labelKey: 'upstreamTier' },
    { id: 'ownership', labelKey: 'ownership' },
    { id: 'businessUnitType', labelKey: 'physicalOrLogical' },
    { id: 'equityShareholding', labelKey: 'equityShareholding' },
    { id: 'financialControl', labelKey: 'financialControl' },
    { id: 'dateOpened', labelKey: 'dateOpened' },
    { id: 'dateClosed', labelKey: 'dateClosed' },
    { id: 'naceCode', labelKey: 'sector' },
    { id: 'naceSection', labelKey: 'industry' },
    { id: 'euTaxonomyCode', labelKey: 'taxonomy' },
    { id: 'profitCenterId', labelKey: 'profitCentreId' },
    { id: 'addressCountry', labelKey: 'country' },
    { id: 'addressState', labelKey: 'state' },
    { id: 'addressCity', labelKey: 'town' },
    { id: 'addressPostCode', labelKey: 'post' },
    { id: 'addressStreet', labelKey: 'street' },
    { id: 'addressBuilding', labelKey: 'building' },
    { id: 'addressCoordinates', labelKey: 'coordinates' },
    { id: 'action', labelKey: 'action' },
  ],
};

const CUSTOMERS_STAKEHOLDERS: TableConfig = {
  prefix: 'customers.fields',
  fields: [
    { id: 'name', labelKey: 'customerName' },
    { id: 'uniqueIdentifier', labelKey: 'uniqueIdentifier' },
    { id: 'code', labelKey: 'customerCode' },
    { id: 'sourceVCMId', labelKey: 'sourceVCMId' },
    { id: 'valueChainSection', labelKey: 'valueChain' },
    { id: 'ownership', labelKey: 'ownership' },
    { id: 'physicalOrLogical', labelKey: 'physicalOrLogical' },
    { id: 'equityShareholding', labelKey: 'equityShareholding' },
    { id: 'financialControl', labelKey: 'financialControl' },
    { id: 'dateOpened', labelKey: 'dateOpened' },
    { id: 'dateClosed', labelKey: 'dateClosed' },
    { id: 'priorityRating', labelKey: 'priorityRating' },
    { id: 'naceCode', labelKey: 'sector' },
    { id: 'naceSection', labelKey: 'industry' },
    { id: 'euTaxonomyCode', labelKey: 'euTaxonomyActivityCode' },
    { id: 'profitCenterId', labelKey: 'profitCentreId' },
    { id: 'addressCountry', labelKey: 'country' },
    { id: 'addressState', labelKey: 'state' },
    { id: 'addressCity', labelKey: 'town' },
    { id: 'addressPostCode', labelKey: 'post' },
    { id: 'addressStreet', labelKey: 'street' },
    { id: 'addressBuilding', labelKey: 'building' },
    { id: 'addressCoordinates', labelKey: 'coordinates' },
    { id: 'action', labelKey: 'action' },
  ],
};

export const DATA_SETS_SUPPLIERS_STAKEHOLDERS_HEAD_CELLS = createHeaderCells(SUPPLIERS_STAKEHOLDERS);
export const DATA_SETS_CUSTOMERS_STAKEHOLDERS_HEAD_CELLS = createHeaderCells(CUSTOMERS_STAKEHOLDERS);

const DISCLOSURE_CONFIG: TableConfig = {
  prefix: 'materialityAssessment.fields',
  fields: [
    { id: 'disclosure', labelKey: 'disclosure' },
    { id: 'category', labelKey: 'category' },
    { id: 'requiredBy', labelKey: 'requiredBy' },
    { id: 'country', labelKey: 'country' },
    { id: 'source', labelKey: 'source' },
    { id: 'valueChainName', labelKey: 'valueChainName' },
  ],
};

export const DISCLOSURE_CELLS = createHeaderCells(DISCLOSURE_CONFIG);

const DISCLOSURE_GROUP_CONFIG: TableConfig = {
  prefix: 'materialityAssessment.fields',
  fields: [
    { id: 'disclosure', labelKey: 'disclosure' },
    { id: 'category', labelKey: 'category' },
    { id: 'requiredBy', labelKey: 'requiredBy' },
    { id: 'country', labelKey: 'country' },
    { id: 'source', labelKey: 'source' },
    { id: 'valueChainName', labelKey: 'valueChainName' },
  ],
};

export const DISCLOSURE_GROUP_CELLS = createHeaderCells(DISCLOSURE_GROUP_CONFIG);

const IRO_TABLE: TableConfig = {
  prefix: 'materialityAssessment.fields',
  fields: [
    { id: 'naceSector', labelKey: 'naceSector' },
    { id: 'naceCode', labelKey: 'naceCode' },
    { id: 'topic', labelKey: 'topic' },
    { id: 'iroCategory', labelKey: 'iroCategory' },
    { id: 'description', labelKey: 'description' },
  ],
};

export const TASK_HEAD_IRO_CELLS = createHeaderCells(IRO_TABLE);

const IRO_MAIN_TABLE: TableConfig = {
  prefix: 'materialityAssessment.fields',
  fields: [
    { id: 'naceSector', labelKey: 'naceSector' },
    { id: 'naceCode', labelKey: 'naceCode' },
    { id: 'reportingFramework', labelKey: 'reportingFramework' },
    { id: 'topic', labelKey: 'topic' },
    { id: 'iroCategory', labelKey: 'iroCategory' },
    { id: 'description', labelKey: 'description' },
    { id: 'impactStatus', labelKey: 'impactStatus' },
    { id: 'actions', labelKey: 'actions' },
  ],
};

export const TASK_HEAD_IRO_MAIN_CELLS = createHeaderCells(IRO_MAIN_TABLE);

const IRO_MAIN_TABLE_EMAILS: TableConfig = {
  prefix: 'materialityAssessment.fields',
  fields: [
    { id: 'respondent', labelKey: 'respondent' },
    { id: 'email', labelKey: 'email' },
    { id: 'iro', labelKey: 'iro' },
    { id: 'title', labelKey: 'title' },
    { id: 'valueChain', labelKey: 'valueChain' },
    { id: 'date', labelKey: 'date' },
    { id: 'status', labelKey: 'status' },
    { id: 'resend', labelKey: 'resend' },
  ],
};

export const HEAD_IRO_MAIN_EMAILS_CELLS = createHeaderCells(IRO_MAIN_TABLE_EMAILS);

const TOPIC_REFINEMENT_CONFIG: TableConfig = {
  prefix: 'materialityAssessment.fields',
  fields: [
    { id: 'topicName', labelKey: 'topicName' },
    { id: 'subTopicName', labelKey: 'subTopicName' },
    { id: 'valueChainName', labelKey: 'valueChainName' },
    { id: 'standardName', labelKey: 'standardName' },
    { id: 'activeDecision', labelKey: 'activeDecision' },
    { id: 'questionCount', labelKey: 'questionCount' },
    { id: 'totalCompaniesCount', labelKey: 'totalCompaniesCount' },
    { id: 'disclosureCount', labelKey: 'disclosureCount' },
    { id: 'totalRegionCount', labelKey: 'totalRegionCount' },
    { id: 'surveyPosition', labelKey: 'surveyPosition' },
    { id: 'interviewsPosition', labelKey: 'interviewsPosition' },
    { id: 'roundTablesPosition', labelKey: 'roundTablesPosition' },
    { id: 'action', labelKey: 'action' },
  ],
};

export const TOPIC_REFINEMENT_CELLS = createHeaderCells(TOPIC_REFINEMENT_CONFIG);

const RESPONDENT_SURVEYS_CONFIG: TableConfig = {
  prefix: 'materialityAssessment.fields',
  fields: [
    { id: 'email', labelKey: 'email' },
    { id: 'topicName', labelKey: 'topicName' },
    { id: 'dateAndTime', labelKey: 'dateAndTime' },
    { id: 'type', labelKey: 'type' },
    { id: 'attachments', labelKey: 'attachments' },
  ],
};

export const TASK_RESPONDENT_SURVEYS_CONFIG_CELLS = createHeaderCells(RESPONDENT_SURVEYS_CONFIG);

const IRO_SEND_LOCAL_TABLE_EMAILS: TableConfig = {
  prefix: 'materialityAssessment.fields',
  fields: [
    { id: 'respondent', labelKey: 'respondent' },
    { id: 'email', labelKey: 'email' },
    { id: 'role', labelKey: 'role' },
  ],
};

export const HEAD_IRO_SEND_LOCAL_EMAILS_CELLS = createHeaderCells(IRO_SEND_LOCAL_TABLE_EMAILS);
